/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();

      // DOM refs
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');

      // Config (from data-attrs)
      this.suggestionsEnabled = this.getAttribute('data-show-on-focus') === 'true';
      this.emptyEnabled = this.getAttribute('data-enable-empty') === 'true';
      this.emptyTemplateId = this.getAttribute('data-empty-template-id');
      this.hasUserInteracted = false;

      // Ensure absolute dropdown anchors below the search bar
      if (this.results && this.results.parentElement) {
        this.results.parentElement.classList.add('relative');
      }

      // Guard: only open after actual user interaction (blocks autoplay focus on load)
      const markInteracted = () => (this.hasUserInteracted = true);
      window.addEventListener('pointerdown', markInteracted, { passive: true });
      window.addEventListener('keydown', markInteracted, { passive: true });

      this.addListeners();
    }

    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    // ========= Core helpers =========
    getQuery() {
      return this.input.value.trim();
    }

    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;
      setTimeout(() => this.statusEl.setAttribute('aria-hidden', 'true'), 1000);
    }

    renderIntoResults(markup, { isSuggestions = false } = {}) {
      this.results.innerHTML = markup;
      this.setAttribute('results', true);
      this.open();
      // In empty state we keep reset hidden (there’s nothing to clear)
      this.resetBtn.hidden = !this.getQuery().length || isSuggestions;
    }

    // ========= Events =========
    handleSubmit(evt) {
      if (
        !this.getQuery().length ||
        this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
      }
    }

    handleInput() {
      this.input.setAttribute('value', this.input.value);
      const q = this.getQuery();

      if (!q.length) {
        if (this.suggestionsEnabled && this.emptyEnabled) {
          this.renderEmptyFromTemplate();
        } else {
          this.close();
        }
        return;
      }

      this.getResults(q);
    }

    handleProductTypeChange(evt) {
      this.productTypeInput && (this.productTypeInput.value = evt.detail.selectedValue);
      const q = this.getQuery();
      if (q.length > 0) {
        this.getResults(q);
      } else if (this.suggestionsEnabled && this.emptyEnabled) {
        this.renderEmptyFromTemplate();
      }
    }

    handleFocus() {
      if (!this.hasUserInteracted) return; // ignore programmatic focus on load
      const q = this.getQuery();

      if (q.length) {
        if (this.getAttribute('results') === 'true') this.open();
        else this.getResults(q);
      } else if (this.suggestionsEnabled && this.emptyEnabled) {
        this.renderEmptyFromTemplate();
      }
    }

    handleKeydown(evt) {
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown': {
          evt.preventDefault();
          if (!this.hasAttribute('open')) return;

          // Prefer active tabpanel; fall back to suggestions list
          const inPanel = this.querySelectorAll(
            '[role="tabpanel"]:not([hidden]) .predictive-search__item'
          );
          const inSuggestions = this.querySelectorAll(
            '.predictive-search__suggestions .predictive-search__item'
          );
          const allResults = inPanel.length ? inPanel : inSuggestions;
          if (!allResults.length) return;

          const selected = this.querySelector(
            '[role="tabpanel"]:not([hidden]) [aria-selected="true"], .predictive-search__suggestions [aria-selected="true"]'
          );
          let next = allResults[0];

          if (evt.key === 'ArrowUp') {
            if (!selected) return;
            next = selected.previousElementSibling || allResults[allResults.length - 1];
          } else {
            next = selected ? (selected.nextElementSibling || allResults[0]) : allResults[0];
          }

          this.statusEl.textContent = '';
          if (next && next !== selected) {
            next.setAttribute('aria-selected', 'true');
            if (selected) selected.setAttribute('aria-selected', 'false');
            this.setLiveRegionText(next.textContent);
            this.input.setAttribute('aria-activedescendant', next.id || '');
          }
          break;
        }
        case 'Enter':
          this.selectOption();
          break;
        case 'Escape':
          this.close();
          break;
      }
    }

    handleKeyup() {
      if (!this.getQuery().length) {
        if (this.suggestionsEnabled && this.emptyEnabled) this.renderEmptyFromTemplate();
        else this.close();
      }
    }

    selectOption() {
      const selected = this.querySelector(
        '[aria-selected="true"] a.js-search-link, [aria-selected="true"] a.predictive-result--suggestion, [aria-selected="true"] a[href]'
      );
      if (selected) selected.click();
    }

    // ========= Empty state from <template> =========
    renderEmptyFromTemplate() {
      const cacheKey =
        '__empty_template__' +
        (this.productTypeInput && this.productTypeInput.value
          ? `:${this.productTypeInput.value}`
          : '');

      if (this.cachedResults[cacheKey]) {
        this.renderIntoResults(this.cachedResults[cacheKey], { isSuggestions: true });
        return;
      }

      this.setLiveRegionLoadingState();

      const tpl = this.emptyTemplateId && document.getElementById(this.emptyTemplateId);
      if (!tpl) {
        // If no template is defined, just close quietly
        this.close();
        return;
      }

      // Use the template’s innerHTML (already built by Liquid with images/prices/etc.)
      const html = tpl.innerHTML;
      this.cachedResults[cacheKey] = html;
      this.renderIntoResults(html, { isSuggestions: true });
    }

    // ========= Predictive results (typed) =========
    async getResults(searchTerm) {
      this.setLiveRegionLoadingState();

      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let params = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        params = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
      } else {
        params = `q=${encodeURIComponent(searchTerm)}`;
      }

      params += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      params += `&${encodeURIComponent('resources[limit_scope]')}=${theme.settings.pSearchLimitScope}`;
      params += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      const sectionId = this.getAttribute('data-results-section') || 'predictive-search';
      params += `&section_id=${encodeURIComponent(sectionId)}`;

      const key = params.replace(' ', '-').toLowerCase();
      if (this.cachedResults[key]) {
        this.renderIntoResults(this.cachedResults[key]);
        return;
      }

      try {
        const res = await fetch(`${theme.routes.predictiveSearch}?${params}`);
        if (!res.ok) throw new Error(res.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await res.text();

        const resultsEl = tmpl.content.querySelector(`#shopify-section-${sectionId}`);
        const html = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        this.cachedResults[key] = html;
        this.renderIntoResults(html);
      } catch (e) {
        this.close();
        throw e;
      }
    }

    // ========= Open / Close =========
    open() {
      this.overlay.classList.add('is-visible');
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    close(evt) {
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
